import React, { useEffect, useState } from 'react'
import { observer } from 'mobx-react-lite'
import { DocumentIcon } from '../../elements/DocumentIcon'
import {
    Row, Col, Button, Text,
    ColorPicker, IconPicker, ProfilePicUploader,
    Nav, NavItem, NavLink, TabContent, TabPane,
    Modal, ModalBody, ModalHeader, ModalFooter
} from '@evertel/web/ui'
import { useObjectUrl } from '@evertel/hooks'
import { APIDataMedia } from '@evertel/types'
import { SchemaBuilderController } from '../../../controller'

interface DocIconPickerProps {
    visible: boolean
    onClose: () => void
    schemaBuilderController: SchemaBuilderController
}

// the icon processor keeps PNG transparency but flattens GIF/WebP to a single JPEG
// frame, so only offer formats that come out the other side looking right
const ICON_IMAGE_TYPES = ['image/jpeg', 'image/png']

interface StagedIcon {
    icon: string
    color: string
    iconMedia: APIDataMedia|null
    iconFile: File|null
}

const DocIconPickerModal: React.FC<DocIconPickerProps> = observer(({
    visible,
    schemaBuilderController,
    onClose
}) => {

    const [activeTab, setActiveTab] = useState<'icon' | 'image'>('icon')

    // local copy of the template's current icon, so nothing reaches the controller until Save
    const currentIcon = (): StagedIcon => ({
        icon: schemaBuilderController.icon,
        color: schemaBuilderController.color,
        iconMedia: schemaBuilderController.iconMedia,
        iconFile: schemaBuilderController.iconFile
    })

    const [staged, setStaged] = useState<StagedIcon>(currentIcon)

    useEffect(() => {
        if (!visible) return

        setActiveTab('icon')
        setStaged(currentIcon())
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [visible])

    const stagedFileUrl = useObjectUrl(staged.iconFile)
    const previewUrl = stagedFileUrl ?? staged.iconMedia?.url

    // a glyph icon and an uploaded image are mutually exclusive
    const onPickGalleryIcon = (icon: string, color: string) => {
        setStaged({ icon, color, iconMedia: null, iconFile: null })
    }

    const onSave = () => {
        schemaBuilderController.setIcon(staged)
        onClose()
    }

    return (
        <Modal
            visible={visible}
            onClose={onClose}
            size="lg"
            style={{ width: '65vw' }}
            alignment="center">
            <ModalHeader title="Template icon" />
            <ModalBody>
                <Row className="mb-3">
                    <DocumentIcon
                        schema={{
                            icon: staged.icon,
                            color: staged.color,
                            iconMedia: { url: previewUrl }
                        }}
                        width={50}
                        height={50}
                    />
                </Row>
                <Nav
                    variant="underline"
                    role="tablist">
                    <NavItem>
                        <NavLink
                            active={activeTab === 'icon'}
                            onClick={() => setActiveTab('icon')}>
                            Icon gallery
                        </NavLink>
                    </NavItem>
                    <NavItem>
                        <NavLink
                            active={activeTab === 'image'}
                            onClick={() => setActiveTab('image')}>
                            Upload image
                        </NavLink>
                    </NavItem>
                </Nav>
                <TabContent>
                    <TabPane
                        role="tabpanel"
                        visible={activeTab === 'icon'}>
                        <Row className="mt-3">
                            <ColorPicker
                                value={staged.color}
                                swatchSize={25}
                                style={{ flex: 1, minWidth: 0 }}
                                className="border-end pe-3"
                                onChange={(color) => onPickGalleryIcon(staged.icon, color)}
                            />
                            <IconPicker
                                value={staged.icon}
                                style={{ flex: 1, minWidth: 0 }}
                                className="ps-3"
                                onChange={(icon) => onPickGalleryIcon(icon, staged.color)}
                            />
                        </Row>
                    </TabPane>
                    <TabPane
                        role="tabpanel"
                        visible={activeTab === 'image'}>
                        <Text
                            tag="h6"
                            bold
                            className="mt-3 mb-3">
                            Upload an image from your device
                        </Text>
                        <Row valign="top">
                            <ProfilePicUploader
                                type="documentSchema"
                                deferred
                                id={schemaBuilderController.id}
                                initialPic={previewUrl}
                                acceptedTypes={ICON_IMAGE_TYPES}
                                onFileSelected={(iconFile) => setStaged(prev => ({ ...prev, iconFile }))}
                            />
                            <Col className="ps-4">
                                <Text tag="p">
                                    Some tips on choosing a good image:
                                </Text>
                                <ul>
                                    <li>File format should be JPG or PNG. Use PNG for a transparent background.</li>
                                    <li>Image dimensions should be 150px square or larger.</li>
                                    <li>File size must be under 10 MB.</li>
                                    <li>Use an image that clearly identifies the template or type of template.</li>
                                </ul>
                            </Col>
                        </Row>
                    </TabPane>
                </TabContent>
            </ModalBody>
            <ModalFooter align="right">
                <Button
                    color="link"
                    onClick={onClose}>
                    Cancel
                </Button>
                <Button
                    color="primary"
                    onClick={onSave}>
                    Done
                </Button>
            </ModalFooter>
        </Modal>
    )
})

export { DocIconPickerModal }
